import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import { useCurrentUserProfile } from "@/hooks/current-user-profile.hook";
import { useRemoveQuestionAnswersMutation } from "@/hooks/remove-question-answers-mutation.hook";
import { useResponseById } from "@/hooks/response-by-id.hook";
import { useSuggestionsByQuestionIdMutation } from "@/hooks/suggestions-by-question-id-mutation.hook";
import { useUpdateQuizAnswerInBatchMutation } from "@/hooks/update-quiz-answer-in-batch-mutation.hook";
import { GetSuggestionsByQuestionIdResponse } from "@/requests/questionnaries/suggestions-by-question-id.request";
import { useQuestion } from "@/store/question.context";
import { DimensionId } from "@/types/questionnaries";
import { LampCharge } from "iconsax-react";
import { FC, HTMLAttributes } from "react";
import { toast } from "sonner";

type ReuseAnswersButtonProps = HTMLAttributes<HTMLDivElement>;

const canReuseAnswers = import.meta.env.VITE_ALLOW_REUSE_ANSWERS === "true";

const ReuseAnswersButton: FC<ReuseAnswersButtonProps> = () => {
  const { responseId, quizId, setIsSuggestionApplied, setIsAutosaveEnabled } =
    useQuestion();

  const { data: userProfile } = useCurrentUserProfile();

  const { data: question } = useResponseById({
    companyId: userProfile?.companyId || "",
    quizId,
    responseId: responseId,
  });

  const suggestionsMutation = useSuggestionsByQuestionIdMutation();

  const removeQuestionAnswersMutation = useRemoveQuestionAnswersMutation();

  const updateAnswersInBatchMutation = useUpdateQuizAnswerInBatchMutation();

  /**
   * Remove all the answers without firing invalidating the question (not changing the state).
   *
   * Error handling should be done in the caller.
   *
   */
  const removeAnswers = async () => {
    if (!question) {
      return Promise.reject();
    }

    try {
      await removeQuestionAnswersMutation.mutateAsync({
        companyId: userProfile?.companyId || "",
        quizId,
        questionId: question.id,
        responseId: question.responseId,
        dimension: question.dimension as DimensionId,
        topic: question.topic,
        questionAllowsAdditionalComments: question.allowAdditionalComments,
        invalidateQuestionResponses: false, // key difference
      });
    } catch {
      return Promise.reject();
    }

    return Promise.resolve();
  };

  const handleReuseAnswers = async () => {
    // If the feature is disabled or the question is not ready, don't do anything
    if (!canReuseAnswers || !question) {
      return;
    }

    // Disable autosave to avoid race conditions
    setIsAutosaveEnabled(false);

    let suggestions: GetSuggestionsByQuestionIdResponse;

    try {
      // Get the suggestions for the question
      suggestions = await suggestionsMutation.mutateAsync({
        companyId: userProfile?.companyId || "",
        questionId: question?.questionId || "",
      });
    } catch {
      setIsAutosaveEnabled(true);

      // Bail early if there are no suggestions
      return;
    }

    try {
      // Clean the answer
      await removeAnswers();

      // Update the answer
      await updateAnswersInBatchMutation.mutateAsync({
        companyId: userProfile?.companyId || "",
        quizId,
        responseId: question.responseId,
        answers: suggestions.answers.map((suggestion) => ({
          choiceId: suggestion.answerId,
          value: suggestion.answerValue || undefined,
          action: "ADD",
        })),
        dimension: question.dimension,
        topic: question.topic,
        invalidateQuestionResponses: true,
      });

      setIsSuggestionApplied(true);

      toast.success("Se aplicaron las respuestas de una edición anterior.");
    } catch {
      toast.error("Fallaron algunas actualizaciones", {
        description:
          "No se pudo actualizar las respuestas correctamente. Por favor verifica las respuestas.",
      });
    }

    setIsAutosaveEnabled(true);
  };

  if (canReuseAnswers) {
    return (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button className="my-2 mr-auto shrink-0">
            Usar respuestas previas <LampCharge size={16} />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reusar respuestas</AlertDialogTitle>
            <AlertDialogDescription>
              Este botón autocompletará las respuestas de esta pregunta con los
              datos de la edición anterior. Asegúrate de revisar cuidadosamente
              la información antes de enviar el cuestionario, ya que la
              información podría haber cambiado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              onClick={() => handleReuseAnswers()}
            >
              Sí, reusar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }
};

export default ReuseAnswersButton;
